import React, { useEffect, useState } from "react";
import { api, rupiah, formatDate } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Card } from "../components/ui/card";
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { TrendingUp, ShoppingBag, Package, AlertTriangle, Users, Loader2 } from "lucide-react";
import { Link } from "react-router-dom";

const KPI = ({ icon: Icon, label, value, tint, testid }) => (
  <Card className="p-5 border-slate-200 rounded-xl shadow-sm" data-testid={testid}>
    <div className="flex items-center justify-between">
      <span className="text-sm text-muted-foreground">{label}</span>
      <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${tint}`}><Icon className="w-4 h-4" /></div>
    </div>
    <div className="mt-3 font-heading text-2xl font-bold tracking-tight">{value}</div>
  </Card>
);

export default function Dashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);

  useEffect(() => {
    api.get("/dashboard/stats").then((r) => setStats(r.data)).catch(() => {});
  }, []);

  if (!stats) return <div className="flex justify-center py-20"><Loader2 className="w-7 h-7 animate-spin text-primary" /></div>;

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      <div>
        <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight">Halo, {user.name} 👋</h1>
        <p className="text-muted-foreground text-sm mt-1">Ringkasan performa toko Anda hari ini.</p>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KPI icon={TrendingUp} label="Penjualan Hari Ini" value={rupiah(stats.today_sales)} tint="bg-accent text-primary" testid="kpi-today-sales" />
        <KPI icon={ShoppingBag} label="Transaksi Hari Ini" value={stats.today_count} tint="bg-sky-100 text-sky-600" testid="kpi-today-count" />
        <KPI icon={Package} label="Total Produk" value={stats.product_count} tint="bg-violet-100 text-violet-600" testid="kpi-products" />
        <KPI icon={Users} label="Pelanggan" value={stats.customers_count} tint="bg-emerald-100 text-emerald-600" testid="kpi-customers" />
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <Card className="lg:col-span-2 p-5 border-slate-200 rounded-xl shadow-sm">
          <h3 className="font-heading font-bold">Penjualan 7 Hari Terakhir</h3>
          <div className="h-64 mt-4">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={stats.chart} margin={{ left: -10, right: 10, top: 10 }}>
                <defs>
                  <linearGradient id="g1" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="hsl(21 90% 48%)" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="hsl(21 90% 48%)" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#eef2f6" vertical={false} />
                <XAxis dataKey="label" tick={{ fontSize: 12, fill: "#64748b" }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 11, fill: "#64748b" }} axisLine={false} tickLine={false} tickFormatter={(v) => v >= 1000 ? `${v / 1000}k` : v} />
                <Tooltip formatter={(v) => rupiah(v)} contentStyle={{ borderRadius: 12, border: "1px solid #e2e8f0", fontSize: 13 }} />
                <Area type="monotone" dataKey="total" stroke="hsl(21 90% 48%)" strokeWidth={2.5} fill="url(#g1)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </Card>

        <Card className="p-5 border-slate-200 rounded-xl shadow-sm">
          <div className="flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 text-amber-500" />
            <h3 className="font-heading font-bold">Stok Menipis</h3>
          </div>
          <div className="mt-4 space-y-2" data-testid="low-stock-list">
            {stats.low_stock_products.length === 0 && <p className="text-sm text-muted-foreground">Semua stok aman 🎉</p>}
            {stats.low_stock_products.map((p) => (
              <div key={p.id} className="flex items-center justify-between p-2.5 rounded-lg bg-amber-50 border border-amber-100">
                <span className="text-sm font-medium truncate">{p.name}</span>
                <span className="text-xs font-bold text-amber-700">Sisa {p.stock}</span>
              </div>
            ))}
          </div>
        </Card>
      </div>

      <Card className="p-5 border-slate-200 rounded-xl shadow-sm">
        <div className="flex items-center justify-between">
          <h3 className="font-heading font-bold">Transaksi Terbaru</h3>
          <Link to="/transactions" className="text-sm text-primary font-semibold">Lihat semua</Link>
        </div>
        <div className="mt-4 overflow-x-auto kasir-scroll">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-muted-foreground border-b border-slate-200">
                <th className="py-2 font-medium">Invoice</th>
                <th className="py-2 font-medium">Waktu</th>
                <th className="py-2 font-medium">Kasir</th>
                <th className="py-2 font-medium text-right">Total</th>
              </tr>
            </thead>
            <tbody>
              {stats.recent_transactions.length === 0 && (
                <tr><td colSpan={4} className="py-6 text-center text-muted-foreground">Belum ada transaksi.</td></tr>
              )}
              {stats.recent_transactions.map((t) => (
                <tr key={t.id} className="border-b border-slate-100 hover:bg-slate-50">
                  <td className="py-2.5 font-medium">{t.invoice_no}</td>
                  <td className="py-2.5 text-muted-foreground">{formatDate(t.created_at)}</td>
                  <td className="py-2.5">{t.cashier_name}</td>
                  <td className="py-2.5 text-right font-semibold">{rupiah(t.total)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
